import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components"
import Header from "./Header"
import List from "./List"
import AdminService from "../../../services/admin.service";
import { Traspass as TraspassType, AgencyTraspass, Payment } from "../../../types/common.types";
import { mobile } from "../../../styles/responsive";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";

interface ReTraspaso {
    id: string
    origin_agency_id: string
    dest_agency_id: string
    traspass_price_in: number
    tickets_in: number
    traspass_price_out: number
    tickets_out: number
    origin_agency: { id: string; name: string; email: string }
    dest_agency: { id: string; name: string; email: string }
}

export default function Traspass() {
    const { setIsLoading } = useGlobal()
    const { user } = useAdmin()
    const [list, setList] = useState<TraspassType[]>([])
    const [payments, setPayments] = useState<Payment[]>([])
    const [reTraspasos, setReTraspasos] = useState<ReTraspaso[]>([])

    useEffect(() => {
        async function getList() {
            setIsLoading(true)
            try {
                const [traspassRes, reRes] = await Promise.all([
                    AdminService.listTraspass(),
                    AdminService.listAllReTraspasos()
                ])
                setList(traspassRes.data.reservations)
                setPayments(traspassRes.data.payments)
                setReTraspasos(reRes.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getList()
    }, [])

    // Step 1: balance from regular reservations (existing logic)
    const balance_array_reservations = list.reduce((acc: AgencyTraspass[], curr: TraspassType) => {
        const amount = curr.balance === 'positive'
            ? curr.traspass_price + curr.tickets
            : -curr.traspass_price - curr.tickets

        const existingItem = acc.find(item =>
            curr.balance === 'positive'
                ? curr.agency.id === item.agency.id
                : curr.agency_trip.id === item.agency.id
        )

        if (existingItem) {
            existingItem.traspass_price += amount
        } else {
            acc.push({
                agency: {
                    id: curr.balance === 'positive' ? curr.agency.id : curr.agency_trip.id,
                    email: curr.balance === 'positive' ? curr.agency.email : curr.agency_trip.email,
                    name: curr.balance === 'positive' ? curr.agency.name : curr.agency_trip.name
                },
                traspass_price: amount
            })
        }
        return acc
    }, [])

    // Step 2: add re-traspasos to balance
    // origin_agency → owes us (positive): traspass_price_in + tickets_in
    // dest_agency   → we owe them (negative): traspass_price_out + tickets_out
    const balance_array_with_retraspasos = reTraspasos.reduce((acc: AgencyTraspass[], rt: ReTraspaso) => {
        const contributions = [
            { agency: rt.origin_agency, amount: rt.traspass_price_in + rt.tickets_in },
            { agency: rt.dest_agency, amount: -(rt.traspass_price_out + rt.tickets_out) }
        ]
        contributions.forEach(({ agency, amount }) => {
            const existing = acc.find(item => item.agency.id === agency.id)
            if (existing) {
                existing.traspass_price += amount
            } else {
                acc.push({ agency, traspass_price: amount })
            }
        })
        return acc
    }, [...balance_array_reservations])

    // Step 3: subtract payments
    const balance_array = balance_array_with_retraspasos.map(obj => {
        const payments_array = payments.filter(p =>
            p.owner === obj.agency.id || p.agency_id === obj.agency.id
        )
        let amount = obj.traspass_price
        for (const payment of payments_array) {
            const type = payment.owner === user?.user.agency_id ? 'send' : 'receive'
            if (type === 'receive') {
                amount = amount - ((payment.amount || 0) + (payment.amount_tickets || 0))
            } else {
                amount = amount + (payment.amount || 0) + (payment.amount_tickets || 0)
            }
        }
        return { ...obj, traspass_price: amount }
    })

    const balance_positive = useMemo(() => {
        return balance_array
            .map(b => b.traspass_price > 0 ? b.traspass_price : 0)
            .reduce((a, b) => a + b, 0)
    }, [balance_array])

    const balance_negative = useMemo(() => {
        return balance_array
            .map(b => b.traspass_price < 0 ? b.traspass_price : 0)
            .reduce((a, b) => a + b, 0)
    }, [balance_array])

    return (
        <Main>
            <Header
                positive={balance_positive}
                negative={balance_negative}
            />
            <List
                list={list}
                payments={payments}
                reTraspasos={reTraspasos}
            />
        </Main>
    )
}

const Main = styled.div`
width: 80%;
margin: auto;
${mobile({
    width: '100%'
})}
`
